import { ModelType } from "./ModelTypes"

export type SchemaValue = {
    name: string,
    description: string,
    createdDate: Date,
    updatedDate: Date,
    datasetSize: number,
    testDatasetSize: number,
    datasetLength: number,
    testDatasetLength: number,
    datasets: [],
    models: TrainedModel[],
    schemaItems: SchemaItem[],
    datasetReportingUpdatedDate: Date,
}

export type CreateSchema = {
    id: string
    name: string
    schemaItems: SchemaItem[]
    countOfClusters?: number
    description?: string
}

export type CreateModel = {
    name: string,
    description: string;
    settings: string,
    schemaId: string,
    modelTypeId: ModelType
    modelDependencies?: ModelDependency[]
}

export type ModelDependency = {
    schemaItemId: string;
    refModelId: string;
}

export type AIDuplicateTrainedModel = {
    name: string,
    description: string,
    id: string,
}

export type AIRunModel = {
    data: unknown[],
    schemaOrdering: string[],
    resultSchemaOrdering: string[]
}

export type TrainedModel = {
    name: string;
    description: string;
    testPercent: number,
    modelTypeId: ModelType,
    modelStateId: AITrainModelStateTypeEnum,
    queueStateId: AITrainModelQueueStateTypeEnum,
    createdDate: Date,
    updatedDate: Date,
    schemaId: string,
    schema: SchemaApiValue,
    isTraining: boolean,
    errorMessage: string,
    metrics: string,
    feature_Metrics: string,
    id: string,
    advancedSettings: string,
    modelHistoryLogs: ModelHistoryLog[]
    modelDependencies?: ModelDependency[]
}

export type ModelHistoryLog = {
    log: string;
    createdDate: Date;
    typeId: ModelHistoryLogTypeEnum;
    modelId: string;
}

export type OnnxUploadStart = {
    totalSize: number;
}

export type OnnxUploadSession = {
    uploadId: string;
    chunkSize: number;
    chunkCount: number;
}

export enum AITrainModelQueueStateTypeEnum {
    WaitForTraining = "WaitForTraining",
    WaitForReTraining = "WaitForReTraining",
    TrainingInProgress = "TrainingInProgress",
    ReTrainingInProgress = "ReTrainingInProgress",
    WaitForMetricsCalculation = "WaitForMetricsCalculation",
    MetricCalculationInProgress = "MetricCalculationInProgress",
    NotInQueue = "NotInQueue",
    WaitForCancellation = "WaitForCancellation",
    WaitForCancellationWithReTraining = "WaitForCancellationWithReTraining"
}

export enum ModelHistoryLogTypeEnum {
    Info = "Info",
    Warning = "Warning",
    Error = "Error",
}

export enum AITrainModelStateTypeEnum {
    Trained = "Trained",
    NotTrained = "NotTrained"
}


export type RefSchema = {
    id?: string;
    schemaItems: SchemaItem[];
}

export type SchemaItem = {
    id?: string
    name: string,
    valueTypeId: StructureItemType,
    typeId: StructureColumnTypeEnum,
    refSchemaId?: string
    refSchema?: RefSchema;
    arrayLength?: number;
    categories?: SchemaItemCategory[];
}

export type FlattenedSchemaItem = {
    key: string;
    schemaItem: SchemaItem;
}

export type SchemaItemCategory = {
    value: string;
    index: number;
}

export enum StructureItemType {
    Number = "Number",
    Category = "Category",
    String = "String",
    NumberArray = "NumberArray",
    Model = "Model"
}

export enum StructureColumnTypeEnum {
    Feature = "Feature",
    Info = "Info",
    Target = "Target"
}

export type AddTrainedModels = {
    id: string
    value: TrainedModel[]
}

export type SchemaApiValue = {
    id: string
} & SchemaValue;

export type SchemaReadmeApiValue = {
    content: string
}

export type SchemaListResponse = {
    data: SchemaApiValue[];
    count: number;
};

export type Model = {
    id: string,
    value: SchemaValue
}

export type Models = {
    [id: string]: SchemaValue
}

export type CreateModelData = BaseDataModel & {
    testPercent: number,
    schemaId: string
}

export type BaseDataModel = {
    data: unknown[],
    schemaOrdering: string[]
}

export type CreateDataItem = {
    schemaId: string,
    isForMetrics: boolean,
    data: Record<string, unknown>
}

export type AdvancedSettingsMap =
{
    settings: { [key: string]: AdvancedSettingTypes }
}

export enum AdvancedSettingTypes {
    Bool = "Bool",
    Float = "Float",
    Int = "Int"
}

export type User = {
    id: string,
    name: string,
    imageUrl: string,
    externalId: string,
    email: string,
    provider: string,
    license: License,
    licenseUsage: LicenseUsage,
    licenseKey: string
}

export type License = {
    countOfModels: number;
    countOfSchemas: number;
    cpuTimeInMinutes: number;
    createdDate: Date;
    datasetSizeForSchemaMb: number;
    expirationDate: Date;
    id: string;
    isCalibratingEnabled: boolean;
    isSelfHosted: boolean;
    isSupportsSeats: boolean;
    licenseType: string;
    licensedUsers: LicensedUser[];
    maxCountModelsInQueue: number;
    numberOfFeatures: number;
    numberOfSeats: number;
    numberOfInstances: number;
    requestsPerMinute: number;
    updatedDate: Date;
    isCancellationRequested: boolean;
}

export type LicenseUsage = {
    numberOfInstances: number;
    countOfSchemas: number;
    cpuUsage: number;
    modelsInQueue: number;
    numberOfSeats: number;
};

export type LicensedUser = {
    externalUserId: string;
    isOwner: boolean;
}

export enum LicenseTypeEnum {
    Trial = "Trial",
    Basic = "Basic",
    Pro = "Pro",
    Enterprise = "Enterprise",
    SelfHostedTrial = "SelfHostedTrial",
    SelfHosted = "SelfHosted"
}

export type AppConfig = {
    isSelfHostedServiceEnabled: boolean;
    isLicenseDisabled: boolean;
    isGoogleEnabled: boolean;
    isSelfHostedServiceLoginEnabled: boolean;
    isGoogleAnalyticsEnabled: boolean;
}

export enum DatasetCleanupType {
    Both = "Both",
    Test = "Test",
    Train = "Train"
}

export type MetricsResult = {
    metrics: string
}